import { nextTick } from 'vue';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import { SetHiddenSearchTermPayload, SetSearchTermPayload } from 'src/stores/filter';
import { useMainLayoutStore } from 'src/stores/mainLayout';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import { routeHasFolderExplorerSource } from 'src/components/workspace/menu/folderExplorerReturnNavigation';

import { ModuleNameEnum } from 'src/models/module/enums';

import { encodeBase64 } from 'src/util/common';
import { EventBus } from 'src/util/eventBus/EventBus';
import { isScreenGtSm } from 'src/util/platform';
import { canUseDesktopSplitView, canUseWorkspaceTabs } from 'src/util/platform/browserInteractionCapabilities';

interface SplitViewDecision {
	createActionsPreferSplitView: boolean;
	desktopSplitViewSupported: boolean;
	isACreateAction: boolean;
	isWorkspaceTabsActive: boolean;
	requestedSplitView: boolean;
	splitViewEnabled: boolean;
}

function parseAggregateModuleName(aggregateId: string): string | undefined {
	try {
		const parsedAggregateId = JSON.parse(aggregateId) as { moduleName?: unknown };
		return typeof parsedAggregateId.moduleName === 'string' ? parsedAggregateId.moduleName : undefined;
	} catch {
		return undefined;
	}
}

export function shouldOpenInSplitView({
	createActionsPreferSplitView,
	desktopSplitViewSupported,
	isACreateAction,
	isWorkspaceTabsActive,
	requestedSplitView,
	splitViewEnabled,
}: SplitViewDecision): boolean {
	if (isWorkspaceTabsActive) {
		if (isACreateAction && !createActionsPreferSplitView) {
			return false;
		}
		return requestedSplitView && splitViewEnabled;
	}
	return desktopSplitViewSupported && requestedSplitView && splitViewEnabled;
}

export function shouldClearAgendaSearchOnMenuClick(
	targetName: string,
	currentRouteName: unknown,
	agendaSearchTerm: string
): boolean {
	return targetName === 'agendaList' && currentRouteName === 'agendaList' && agendaSearchTerm.trim() !== '';
}

export function clearAgendaSearch(
	filterStore: {
		setHiddenSearchTerm: (payload: SetHiddenSearchTermPayload) => unknown;
		setSearchTerm: (payload: SetSearchTermPayload) => unknown;
	},
	eventBus: { emit: (eventName: string) => void }
): void {
	void filterStore.setSearchTerm({ module: 'agendaList', searchTerm: '' });
	void filterStore.setHiddenSearchTerm({ hiddenSearchTerm: '', module: 'agendaList' });
	eventBus.emit('clearTheSearchTerm');
}

export function openDetails(
	router: Router,
	route: RouteLocationNormalizedLoaded,
	_store: unknown,
	eventBus: EventBus,
	shouldUseTheSplitView: boolean,
	label: string,
	aggregateId: string,
	moduleName: string,
	activityListLocation: string,
	props?: Record<string, unknown>
): void {
	internalOpenDetailsAndEditOrView(
		router,
		route,
		_store,
		eventBus,
		shouldUseTheSplitView,
		label,
		aggregateId,
		moduleName,
		activityListLocation,
		false,
		props
	);
}

export function openDetailsAndEdit(
	router: Router,
	route: RouteLocationNormalizedLoaded,
	_store: unknown,
	eventBus: EventBus,
	shouldUseTheSplitView: boolean,
	label: string,
	aggregateId: string,
	moduleName: string,
	activityListLocation: string,
	props?: Record<string, unknown>
): void {
	internalOpenDetailsAndEditOrView(
		router,
		route,
		_store,
		eventBus,
		shouldUseTheSplitView,
		label,
		aggregateId,
		moduleName,
		activityListLocation,
		true,
		props
	);
}

function internalOpenDetailsAndEditOrView(
	router: Router,
	route: RouteLocationNormalizedLoaded,
	_store: unknown,
	eventBus: EventBus,
	shouldUseTheSplitView: boolean,
	label: string,
	aggregateId: string,
	moduleName: string,
	activityListLocation: string,
	edit: boolean,
	props?: Record<string, unknown>
): void {
	const mainLayoutStore = useMainLayoutStore();
	const userInterfaceSettingsStore = useUserInterfaceSettingsStore();
	const splitViewEnabled = userInterfaceSettingsStore.splitViewEnabled;
	const aggregateModuleName = parseAggregateModuleName(aggregateId);
	const openInSplitView = shouldOpenInSplitView({
		createActionsPreferSplitView: userInterfaceSettingsStore.workspaceTabsCreateActionsInSplitView,
		desktopSplitViewSupported: canUseDesktopSplitView(isScreenGtSm()),
		isACreateAction: props?.isACreateAction === true && props?.openCreateInSplitView !== true,
		isWorkspaceTabsActive: canUseWorkspaceTabs(
			userInterfaceSettingsStore.workspaceTabsEnabled,
			splitViewEnabled,
			isScreenGtSm()
		),
		requestedSplitView: shouldUseTheSplitView,
		splitViewEnabled,
	});
	const detailRouteQuery = routeHasFolderExplorerSource(route) ? { source: 'folderExplorer' } : undefined;

	if (openInSplitView) {
		void mainLayoutStore.setSplitViewActive(true);
		void nextTick(() => {
			eventBus.emit(
				'openDetailsTab',
				route.name as string,
				aggregateId,
				label,
				activityListLocation,
				edit ? 'edit' : 'view',
				props
			);

			eventBus.emit('restoreTheActivityListScrollPostionAndGrouping');
		});
	} else {
		if (moduleName == ModuleNameEnum.MODULE_APPOINTMENT) {
			void router.push({
				name: 'appointment',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					...props,
				},
				query: detailRouteQuery,
			});
		} else if (moduleName == ModuleNameEnum.MODULE_EMAIL) {
			if (aggregateModuleName == 'mail_create') {
				void router.push({
					name: 'mailSend',
				});
			} else {
				void router.push({
					name: 'mail',
					params: {
						activityListLocation: activityListLocation,
						editOrView: edit ? 'edit' : 'view',
						id: encodeBase64(aggregateId),
						...props,
					},
					query: detailRouteQuery,
				});
			}
		} else if (moduleName == ModuleNameEnum.MODULE_PHONECALL) {
			if (aggregateModuleName == 'phone_call_create') {
				void router.push({
					name: 'phoneCallCreate',
					params: {
						desiredPhoneNumberToContact: <string>props?.desiredPhoneNumberToContact,
						id: props?.id as string,
					},
				});
			} else if (aggregateModuleName == 'phone_call_phone_book') {
				void router.push({
					name: 'PhoneBook',
				});
			} else {
				void router.push({
					name: 'phoneCall',
					params: {
						activityListLocation: activityListLocation,
						editOrView: edit ? 'edit' : 'view',
						id: encodeBase64(aggregateId),
						...props,
					},
					query: detailRouteQuery,
				});
			}
		} else if (moduleName == ModuleNameEnum.MODULE_ACTIVITY) {
			void router.push({
				name: 'memo',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					...props,
				},
				query: detailRouteQuery,
			});
		} else if (moduleName == ModuleNameEnum.MODULE_MESSAGE) {
			void router.push({
				name: 'message',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					...props,
				},
				query: detailRouteQuery,
			});
		} else if (moduleName == ModuleNameEnum.MODULE_TASK) {
			void router.push({
				name: 'task',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					...props,
				},
				query: detailRouteQuery,
			});
		} else if (moduleName == ModuleNameEnum.MODULE_NOTE) {
			void router.push({
				name: 'note',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					...props,
				},
				query: detailRouteQuery,
			});
		} else if (moduleName == ModuleNameEnum.MODULE_DOCUMENT) {
			void router.push({
				name: 'document',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					...props,
				},
				query: detailRouteQuery,
			});
		} else if (moduleName == ModuleNameEnum.MODULE_IDO) {
			void router.push({
				name: 'record',
				params: {
					activityListLocation: activityListLocation,
					editOrView: edit ? 'edit' : 'view',
					id: encodeBase64(aggregateId),
					tab: 'info',
					...props,
				},
				query: detailRouteQuery,
			});
		}
	}
}

export function iconForRoute(route: RouteLocationNormalizedLoaded): string {
	let icon = '';
	switch (String(route.name)) {
		case 'folderExplorer':
		case 'organization':
			icon = 'fa-sitemap';
			break;

		case 'notebookList':
			icon = 'fa-folder';
			break;

		case 'inbox':
			icon = 'fa-house';
			break;

		case 'filterList':
			icon = 'fa-globe';
			break;

		case 'task':
		case 'taskList':
			icon = 'fa-square-check';
			break;

		case 'agenda':
		case 'agendaList':
		case 'appointment':
			icon = 'fa-calendar-days';
			break;

		case 'record':
		case 'records':
			icon = 'fa-notebook';
			break;

		case 'phoneCall':
		case 'phoneCallList':
			icon = 'fa-phone';
			break;

		case 'mail':
		case 'mailList':
			icon = 'fa-envelope';
			break;

		case 'message':
		case 'messageList':
			icon = 'fa-comment-alt';
			break;

		case 'document':
		case 'documentList':
			icon = 'fa-file-lines';
			break;

		case 'note':
		case 'noteList':
			icon = 'fa-pen-to-square';
			break;

		case 'trash':
		case 'trashList':
			icon = 'fa-trash';
			break;

		case 'userOnline':
			icon = 'fa-user';
			break;

		case 'settings':
			icon = 'fa-gear';
			break;
	}

	return icon;
}

export function mainMenuItemNameForRoute(route: RouteLocationNormalizedLoaded): string {
	const mainMenuItemName = route.meta.mainMenuItemName;
	if (typeof mainMenuItemName === 'string') {
		return mainMenuItemName;
	}
	return typeof route.name === 'string' ? route.name : '';
}
